import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import deliveryService from '../../services/deliveryService';
import imageUploadClient from '../../services/imageUploadClient';
import { generateId } from '../../utils/generateId';
import ConfirmModal from '../shared/ConfirmModal';
import MessageModal from '../shared/MessageModal';
import DeliveryStatusBadge from './DeliveryStatusBadge';
import DeliveryDelayedBadge from './DeliveryDelayedBadge';
import { getDeliveryStatusMeta } from './statusConfig';
import { TimelineSection, TimelineTitle, TimelineTitleRow } from './DeliveryStatusTimeline.styles';
import { CrudStyles } from '../shared/Crud';
import {
    ActionsRow,
    AddPhotoBtn,
    DeParaFlow,
    FlowArrow,
    FlowBlock,
    FlowLabel,
    IntroText,
    LoadingGrid,
    LoadingSkeleton,
    MessageBox,
    MessageBoxError,
    PhotoCountBadge,
    PhotoGrid,
    PhotoRemoveBtn,
    PhotoSection,
    PhotoSectionHeader,
    PhotoSectionLabel,
    PhotoThumbImg,
    PhotoThumbWrapper,
    PreviewPlaceholder,
    StatusOptionsGrid,
    StatusOptionTile,
    SubsectionTitle,
    TileLabel,
} from './DeliveryStatusUpdateSection.styles';

const { Button } = CrudStyles;

const SECTION_TITLE_ID = 'delivery-status-update-title';
const NEW_STATUS_LABEL_ID = 'delivery-status-new-label';
const MAX_PHOTOS = 15;

function countHistoryPhotos(history) {
    if (!Array.isArray(history)) return 0;
    return history.reduce((sum, entry) => {
        const photos = Array.isArray(entry?.photos) ? entry.photos : [];
        return sum + photos.length;
    }, 0);
}

/**
 * Seção de consulta e alteração de status (apenas empresa de transporte).
 * Deve aparecer antes do histórico na tela de consulta.
 *
 * @param {{ deliveryId: string|number, currentState: string, customerIdForApi: string|null|undefined, history?: Array, deliveryRoutes?: Array, delayed?: boolean, onStatusUpdated?: () => void|Promise<void> }} props
 */
export default function DeliveryStatusUpdateSection({
    deliveryId,
    currentState,
    customerIdForApi,
    history = [],
    deliveryRoutes = [],
    delayed = false,
    onStatusUpdated,
}) {
    const [possible, setPossible] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [optionsError, setOptionsError] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [selected, setSelected] = useState('');
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });

    const optionRefs = useRef([]);
    const fileInputRef = useRef(null);

    useEffect(() => {
        optionRefs.current = [];
    }, [possible]);

    useEffect(() => {
        setSelected('');
        setOptionsError(null);
        if (!currentState) {
            setPossible([]);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoadingOptions(true);
            try {
                const statuses = await deliveryService.getPossibleDeliveryStatuses(currentState);
                if (!cancelled) {
                    setPossible(Array.isArray(statuses) ? statuses : []);
                }
            } catch (err) {
                if (!cancelled) {
                    setPossible([]);
                    setOptionsError(err?.message || 'Não foi possível carregar os status permitidos.');
                }
            } finally {
                if (!cancelled) {
                    setLoadingOptions(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentState, deliveryId]);

    useEffect(() => {
        return () => {
            attachedFiles.forEach((f) => URL.revokeObjectURL(f.previewUrl));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selected) return undefined;
        setAttachedFiles((prev) => {
            prev.forEach((f) => URL.revokeObjectURL(f.previewUrl));
            return [];
        });
        return undefined;
    }, [selected]);

    const sortedPossible = useMemo(
        () => [...possible].sort((a, b) => String(a).localeCompare(String(b))),
        [possible]
    );

    const existingPhotoCount = useMemo(() => countHistoryPhotos(history), [history]);

    const hasRoutePoints = useMemo(
        () => deliveryService.hasDeliveryRoutePoints(deliveryRoutes),
        [deliveryRoutes]
    );

    const canSubmit =
        Boolean(selected && customerIdForApi && deliveryId && selected !== currentState && !updating);

    const handleFileChange = useCallback(
        (e) => {
            const incoming = Array.from(e.target.files || []);
            if (!incoming.length) return;

            setAttachedFiles((prev) => {
                const remaining = MAX_PHOTOS - existingPhotoCount - prev.length;
                if (remaining <= 0) return prev;
                const toAdd = incoming.slice(0, remaining).map((file) => ({
                    localId: generateId('photo'),
                    file,
                    previewUrl: URL.createObjectURL(file),
                }));
                return [...prev, ...toAdd];
            });

            e.target.value = '';
        },
        [existingPhotoCount]
    );

    const handleRemovePhoto = useCallback((localId) => {
        setAttachedFiles((prev) => {
            const item = prev.find((f) => f.localId === localId);
            if (item) URL.revokeObjectURL(item.previewUrl);
            return prev.filter((f) => f.localId !== localId);
        });
    }, []);

    const submitStatusUpdate = useCallback(async () => {
        setUpdating(true);
        setUploadProgress(null);

        try {
            let imageIds = [];

            if (attachedFiles.length > 0) {
                const endpoint = deliveryService.buildDeliveryImagesEndpoint(deliveryId, customerIdForApi);
                const files = attachedFiles.map((f) => f.file);

                imageIds = await imageUploadClient.uploadAll(endpoint, files, {
                    onProgress: (sent, total) => {
                        setUploadProgress({ sent, total });
                    },
                });

                attachedFiles.forEach((f) => URL.revokeObjectURL(f.previewUrl));
                setAttachedFiles([]);
                setUploadProgress(null);
            }

            await deliveryService.updateDeliveryStatus(deliveryId, selected, customerIdForApi, imageIds);

            if (onStatusUpdated) {
                await onStatusUpdated();
            }
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao atualizar o status da entrega.',
            });
        } finally {
            setUpdating(false);
            setUploadProgress(null);
        }
    }, [attachedFiles, customerIdForApi, deliveryId, onStatusUpdated, selected]);

    const handleUpdateClick = () => {
        if (!canSubmit) return;
        if (hasRoutePoints) {
            setConfirmOpen(true);
            return;
        }
        submitStatusUpdate();
    };

    const handleConfirmStatusUpdate = () => {
        setConfirmOpen(false);
        submitStatusUpdate();
    };

    const getRadioTabIndex = useCallback(
        (index) => {
            if (sortedPossible.length === 0) return -1;
            const selIdx = sortedPossible.indexOf(selected);
            if (selIdx >= 0) return index === selIdx ? 0 : -1;
            return index === 0 ? 0 : -1;
        },
        [sortedPossible, selected]
    );

    const focusRadioAt = useCallback((index) => {
        const len = sortedPossible.length;
        if (len === 0) return;
        const i = ((index % len) + len) % len;
        optionRefs.current[i]?.focus();
    }, [sortedPossible.length]);

    const handleRadioGroupKeyDown = useCallback(
        (event) => {
            if (!sortedPossible.length || updating) return;
            const refs = optionRefs.current;
            const activeIndex = refs.findIndex((el) => el === document.activeElement);
            const i = activeIndex >= 0 ? activeIndex : 0;

            switch (event.key) {
                case 'ArrowRight':
                case 'ArrowDown':
                    event.preventDefault();
                    focusRadioAt(i + 1);
                    break;
                case 'ArrowLeft':
                case 'ArrowUp':
                    event.preventDefault();
                    focusRadioAt(i - 1);
                    break;
                case 'Home':
                    event.preventDefault();
                    focusRadioAt(0);
                    break;
                case 'End':
                    event.preventDefault();
                    focusRadioAt(sortedPossible.length - 1);
                    break;
                case ' ':
                case 'Enter':
                    event.preventDefault();
                    if (activeIndex >= 0) {
                        setSelected(sortedPossible[activeIndex]);
                    }
                    break;
                default:
                    break;
            }
        },
        [sortedPossible, focusRadioAt, updating]
    );

    const setOptionRef = useCallback((el, index) => {
        optionRefs.current[index] = el;
    }, []);

    const photoCount = existingPhotoCount + attachedFiles.length;
    const atLimit = photoCount >= MAX_PHOTOS;

    const updatingLabel = (() => {
        if (!updating) return 'Atualizar status';
        if (uploadProgress) return `Enviando fotos (${uploadProgress.sent}/${uploadProgress.total})…`;
        if (attachedFiles.length > 0) return 'Preparando…';
        return 'Atualizando…';
    })();

    return (
        <TimelineSection>
            <TimelineTitleRow>
                <TimelineTitle id={SECTION_TITLE_ID}>Status da entrega</TimelineTitle>
                <DeliveryDelayedBadge delayed={delayed} />
            </TimelineTitleRow>
            <IntroText>
                Status atual registrado no sistema. Quando houver transições permitidas, você pode definir um novo
                status; o histórico abaixo será atualizado após a confirmação.
            </IntroText>

            {hasRoutePoints ? (
                <MessageBox $variant="warning" role="status">
                    Esta entrega possui pontos de roteiro associados. Ao atualizar o status por esta tela, esses
                    pontos serão cancelados.
                </MessageBox>
            ) : null}

            <DeParaFlow>
                <FlowBlock>
                    <FlowLabel>Atual</FlowLabel>
                    <DeliveryStatusBadge state={currentState} compact />
                </FlowBlock>
                <FlowArrow aria-hidden>→</FlowArrow>
                <FlowBlock aria-live="polite" aria-atomic="true">
                    <FlowLabel>Novo</FlowLabel>
                    {selected ? (
                        <DeliveryStatusBadge state={selected} compact />
                    ) : (
                        <PreviewPlaceholder>Escolha um status abaixo</PreviewPlaceholder>
                    )}
                </FlowBlock>
            </DeParaFlow>

            <SubsectionTitle id={NEW_STATUS_LABEL_ID}>Alterar para</SubsectionTitle>

            {loadingOptions ? (
                <LoadingGrid aria-busy="true" aria-label="Carregando opções de status">
                    <LoadingSkeleton />
                    <LoadingSkeleton />
                    <LoadingSkeleton />
                </LoadingGrid>
            ) : null}

            {optionsError ? <MessageBoxError role="alert">{optionsError}</MessageBoxError> : null}

            {!loadingOptions && !optionsError && sortedPossible.length === 0 ? (
                <MessageBox>
                    Não há outros status disponíveis a partir de &quot;{getDeliveryStatusMeta(currentState).label}
                    &quot;.
                </MessageBox>
            ) : null}

            {!loadingOptions && !optionsError && sortedPossible.length > 0 ? (
                <StatusOptionsGrid
                    role="radiogroup"
                    aria-labelledby={`${SECTION_TITLE_ID} ${NEW_STATUS_LABEL_ID}`}
                    onKeyDown={handleRadioGroupKeyDown}
                >
                    {sortedPossible.map((s, index) => {
                        const meta = getDeliveryStatusMeta(s);
                        const isChecked = selected === s;
                        const Icon = meta.icon;
                        return (
                            <StatusOptionTile
                                key={s}
                                ref={(el) => setOptionRef(el, index)}
                                type="button"
                                role="radio"
                                aria-checked={isChecked}
                                tabIndex={getRadioTabIndex(index)}
                                $colorToken={meta.colorToken}
                                disabled={updating}
                                onClick={() => setSelected(s)}
                            >
                                {Icon ? <Icon /> : null}
                                <TileLabel>{meta.label || s}</TileLabel>
                            </StatusOptionTile>
                        );
                    })}
                </StatusOptionsGrid>
            ) : null}

            {selected ? (
                <PhotoSection>
                    <PhotoSectionHeader>
                        <PhotoSectionLabel>Fotos (opcional)</PhotoSectionLabel>
                        <PhotoCountBadge $warn={atLimit}>
                            {photoCount}/{MAX_PHOTOS}
                        </PhotoCountBadge>
                    </PhotoSectionHeader>

                    <PhotoGrid $hasItems={attachedFiles.length > 0}>
                        {attachedFiles.map(({ localId, previewUrl, file }) => (
                            <PhotoThumbWrapper key={localId}>
                                <PhotoThumbImg src={previewUrl} alt={file.name} title={file.name} />
                                {!updating && (
                                    <PhotoRemoveBtn
                                        type="button"
                                        aria-label={`Remover foto ${file.name}`}
                                        onClick={() => handleRemovePhoto(localId)}
                                    >
                                        ×
                                    </PhotoRemoveBtn>
                                )}
                            </PhotoThumbWrapper>
                        ))}

                        {!atLimit && (
                            <AddPhotoBtn
                                type="button"
                                aria-label="Adicionar foto"
                                title="Adicionar foto"
                                disabled={updating}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                +
                            </AddPhotoBtn>
                        )}
                    </PhotoGrid>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                        multiple
                        hidden
                        onChange={handleFileChange}
                    />
                </PhotoSection>
            ) : null}

            {customerIdForApi == null || String(customerIdForApi).trim() === '' ? (
                <MessageBoxError role="status">
                    Não foi possível determinar o cliente da entrega para enviar a atualização. Abra esta entrega a
                    partir da lista (com o cliente identificado) ou verifique os dados carregados.
                </MessageBoxError>
            ) : null}

            <ActionsRow>
                <Button type="button" variant="primary" disabled={!canSubmit} onClick={handleUpdateClick}>
                    {updatingLabel}
                </Button>
            </ActionsRow>

            <ConfirmModal
                open={confirmOpen}
                title="Confirmar atualização de status"
                message={
                    'Esta entrega possui pontos de roteiro associados. Ao atualizar o status por esta tela, ' +
                    'esses pontos serão cancelados.\n\nDeseja continuar?'
                }
                confirmLabel="Atualizar status"
                cancelLabel="Cancelar"
                confirmVariant="primary"
                onConfirm={handleConfirmStatusUpdate}
                onCancel={() => setConfirmOpen(false)}
            />

            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
        </TimelineSection>
    );
}
